import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

/** What a section component gets: its own path (one component may serve several, e.g. notification kinds). */
export interface SectionProps {
  section: string
}

export interface SectionDef {
  /** OnlyFans' path under /my/settings (also our `?s=` value). */
  id: string
  /** The left-menu entry it belongs to (highlighted while it is open). */
  nav: NavId
  /** Account owners and super admins only (Account security). */
  ownerOnly?: boolean
  component: LazyExoticComponent<ComponentType<SectionProps>>
}

/** The left menu, in OnlyFans' order: the account's own block, then General. */
export const NAV_ACCOUNT = ['profile', 'account', 'security', 'subscription', 'fans', 'notifications', 'messaging', 'story', 'streaming'] as const
export const NAV_GENERAL = ['display', 'qrcode'] as const
export type NavId = (typeof NAV_ACCOUNT)[number] | (typeof NAV_GENERAL)[number]

/** Where a top-level entry opens (OnlyFans sends Subscription to its first tab). */
export const NAV_TARGET: Partial<Record<NavId, string>> = { subscription: 'subscription/promotion-campaign' }

const s = (id: string, nav: NavId, load: () => Promise<{ default: ComponentType<SectionProps> }>, ownerOnly?: boolean): SectionDef => ({
  id,
  nav,
  ownerOnly,
  component: lazy(load),
})

const Subscription = () => import('./subscription/Subscription')
const NotificationKind = () => import('./general/NotificationKind')

export const SECTIONS: readonly SectionDef[] = [
  // Profile and what it links to
  s('profile', 'profile', () => import('./profile/EditProfile')),
  s('social-media', 'profile', () => import('./profile/SocialMedia')),
  s('spotify', 'profile', () => import('./profile/Spotify')),
  s('spring-store', 'profile', () => import('./profile/SpringStore')),
  s('links', 'profile', () => import('./profile/ThingsILike')),
  s('ethwallet', 'profile', () => import('./profile/Web3Avatar')),
  s('nft', 'profile', () => import('./profile/Nfts')),
  // Account
  s('account', 'account', () => import('./account/Account')),
  s('account/username', 'account', () => import('./account/Username')),
  s('account/email', 'account', () => import('./account/Email'), true),
  s('account/phone', 'account', () => import('./account/Phone'), true),
  s('account/x', 'account', () => import('./account/XAccount'), true),
  s('account/google', 'account', () => import('./account/Google'), true),
  s('account/connected', 'account', () => import('./account/Connected'), true),
  s('account/password', 'account', () => import('./account/Password'), true),
  s('account/sessions', 'account', () => import('./account/Sessions'), true),
  s('account/2fa', 'account', () => import('./account/TwoStep'), true),
  s('account/delete', 'account', () => import('./account/DeleteAccount'), true),
  // Privacy and safety, Fans and following, Chats, Story
  s('security', 'security', () => import('./privacy/Privacy')),
  s('security/reserved_usernames', 'security', () => import('./privacy/ReservedUsernames')),
  s('security/watermarks', 'security', () => import('./privacy/Watermarks')),
  s('security/block_by_country', 'security', () => import('./privacy/BlockByCountry')),
  s('security/block_by_ip', 'security', () => import('./privacy/BlockByIp')),
  s('fans', 'fans', () => import('./privacy/Fans')),
  s('messaging', 'messaging', () => import('./privacy/Chats')),
  s('story', 'story', () => import('./privacy/Story')),
  // Subscription price and bundles (three tabs of one page)
  s('subscription/promotion-campaign', 'subscription', Subscription),
  s('subscription/trial-links', 'subscription', Subscription),
  s('subscription/tracking-links', 'subscription', Subscription),
  // Notifications, Streaming, Display, QR code
  s('notifications', 'notifications', () => import('./general/Notifications')),
  s('notifications/webpush', 'notifications', NotificationKind),
  s('notifications/email', 'notifications', NotificationKind),
  s('notifications/message', 'notifications', NotificationKind),
  s('notifications/toast', 'notifications', NotificationKind),
  s('notifications/mentions', 'notifications', NotificationKind),
  s('streaming', 'streaming', () => import('./general/Streaming')),
  s('streaming/blocked', 'streaming', () => import('./general/StreamingBlocked')),
  s('display', 'display', () => import('./general/Display')),
  s('display/language', 'display', () => import('./general/Language')),
  s('qrcode', 'qrcode', () => import('./general/QrCode')),
]

const BY_ID = new Map(SECTIONS.map((d) => [d.id, d]))

/** The section for a `?s=` value (trailing slashes and a bare `subscription` tolerated), or null. */
export function findSection(raw: string | null | undefined): SectionDef | null {
  const id = String(raw ?? '')
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase()
  if (!id) return null
  return BY_ID.get(NAV_TARGET[id as NavId] ?? id) ?? null
}
